import {
  type ActionCard,
  ChatStatus,
  type Message as TMessage,
  useStores,
} from '@littlebird/sdk';
import {
  findLinkedActionCardIds,
  isContentOldEnough,
} from '@littlebird/ui-utils';
import { observer } from 'mobx-react-lite';
import { memo, useEffect, useRef, useState } from 'react';

import CopyButton from '@/components/blocks/CopyButton';
import {
  Attachments,
  PendingAssistantFiles,
} from '@/components/blocks/Message/Attachments';
import { useAssistantMessageCallbacks } from '@/components/blocks/Message/useAssistantMessageCallbacks';
import { XStack, YStack } from '@/components/ui';
import { Markdown } from '@/components/ui/smoothdown';
import { ActionCardItem } from '@/features/action-cards/components/ActionCardItem';
import {
  AskUserAnswersSummary,
  AskUserPicker,
  findAskUserCard,
} from '@/features/ask-user';
import { usePendingSandboxFiles } from '@/features/chat/chat.hooks';
import {
  HivemindIncomingMessage,
  hivemindIncomingSenderOf,
} from '@/features/hivemind';
import { useAppMentionLinks } from '@/features/mention';
import { usePersonMentionLinks } from '@/features/people-wiki/mentions';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { Bubble, BubbleContent } from './agents-bubble';
import { Message, MessageContent } from './agents-message-row';
import {
  actionCardSegmentIds,
  holdStreamedSegments,
  isConnectionCardSegment,
  splitAroundActionCardLines,
  splitIntoBubbleSegments,
  stripActionCardLinks,
} from './agents.utils';

const ENTRANCE_STAGGER_MS = 1000;
const messageMaxWidth = 'max-w-[min(85%,48rem)]';

type SegmentView =
  | { kind: 'cards'; cards: ActionCard[] }
  | { kind: 'connection'; text: string }
  | { kind: 'bubble'; text: string };

type AgentMessageProps = {
  threadId: string;
  messageIndex: number;
  message: TMessage;
  cards?: ActionCard[];
  isLastAssistantMessage?: boolean;
  precedesAssistantMessage?: boolean;
  animateOnAppear?: boolean;
  onAskUserComplete?: () => void;
};

const AgentMessage = observer((props: AgentMessageProps) => {
  const incomingSender = hivemindIncomingSenderOf(props.message);
  if (incomingSender) {
    return (
      <HivemindIncomingMessage
        threadId={props.threadId}
        message={props.message}
        messageIndex={props.messageIndex}
        sender={incomingSender}
      />
    );
  }
  return props.message.role === 'assistant' ? (
    <AssistantMessage {...props} />
  ) : (
    <UserMessage {...props} />
  );
});

export default AgentMessage;

const UserMessage = observer(
  ({ threadId, message, messageIndex }: AgentMessageProps) => {
    const { actionCards } = useStores();
    const { quotes, body } = message;
    const displayBody = useAppMentionLinks(
      usePersonMentionLinks(body, { enabled: true }),
      { enabled: true }
    );

    return (
      <Message
        align="end"
        data-message-index={messageIndex}
        data-message-id={message.id}
        data-role="user"
        className="mb-5"
      >
        <MessageContent>
          <Attachments
            fileIds={message.fileIds}
            isAssistant={false}
            key={message.id}
          />

          {message.askUserAnswers &&
            !findAskUserCard(
              actionCards.getByChatId(threadId),
              message.askUserAnswers.request_id
            ) && (
              <div className="flex w-full justify-end">
                <div className={messageMaxWidth}>
                  <AskUserAnswersSummary answers={message.askUserAnswers} />
                </div>
              </div>
            )}

          {(body || quotes.length > 0) && (
            <Bubble align="end" className={messageMaxWidth}>
              {body && (
                <XStack className="pointer-events-none absolute top-1/2 right-full mr-3.5 -translate-y-1/2 items-center gap-0.5 opacity-0 touch:pointer-events-auto touch:opacity-100 group-hover/message:pointer-events-auto group-hover/message:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/message:opacity-100 group-has-[:focus-visible]/message:pointer-events-auto">
                  <CopyButton
                    text={body}
                    copyText="Copy text"
                    className="flex"
                    size="xs"
                    onCopy={() => {
                      trackEvent('MESSAGE_LB_COPY_CLICKED', {
                        chatId: threadId,
                        messageIndex,
                      });
                    }}
                  />
                </XStack>
              )}
              <BubbleContent
                data-allow-selection
                className="rounded-2xl px-3.5 py-1.5 [&_.prose]:text-[13px] smoothing-default"
              >
                {quotes.length > 0 && (
                  <XStack
                    className={cn('items-stretch gap-4 py-1', body && 'mb-2')}
                  >
                    <span className="w-1 shrink-0 bg-border-strongest rounded-full" />
                    <YStack className="flex-1 gap-1">
                      {quotes.map((quote, index) => (
                        <span
                          key={index}
                          className="text-base text-foreground leading-relaxed"
                        >
                          {quote}
                        </span>
                      ))}
                    </YStack>
                  </XStack>
                )}
                <Markdown
                  id={`thread-${threadId}-user-message-${message.id}`}
                  content={displayBody}
                />
              </BubbleContent>
            </Bubble>
          )}
        </MessageContent>
      </Message>
    );
  }
);

const AssistantMessage = observer(
  ({
    threadId,
    message,
    messageIndex,
    cards = [],
    isLastAssistantMessage = false,
    precedesAssistantMessage = false,
    animateOnAppear = false,
    onAskUserComplete,
  }: AgentMessageProps) => {
    const { messages: messagesStore, actionCards } = useStores();
    const text = message.displayContent;
    const bubbleText = stripActionCardLinks(text);
    const liveAskUserRequest = messagesStore.getLiveAskUserRequest(threadId);
    const askUserRequest =
      message.askUserQuestions &&
      liveAskUserRequest?.request_id === message.askUserQuestions.request_id &&
      !findAskUserCard(
        actionCards.getByChatId(threadId),
        message.askUserQuestions.request_id
      )
        ? message.askUserQuestions
        : null;
    const hasFiles = message.fileIds?.length > 0;
    const animatesOnAppearRef = useRef(
      animateOnAppear && !isContentOldEnough(message.createdAt, 60_000)
    );
    const pendingSandboxFiles = usePendingSandboxFiles(
      threadId,
      message.id,
      isLastAssistantMessage
    );
    const showControls =
      message.status === ChatStatus.Done ||
      message.status === ChatStatus.Stopped ||
      message.status === ChatStatus.Errored;

    const segmentCardIds = new Map<string, number[] | null>();
    const allSegments = text
      ? splitAroundActionCardLines(text).flatMap((piece) => {
          const cardIds = actionCardSegmentIds(piece);
          if (!cardIds) return splitIntoBubbleSegments(piece);
          segmentCardIds.set(piece, cardIds);
          return [piece];
        })
      : [];
    const linkedLocalIds = findLinkedActionCardIds(text);
    const cardsByLocalId = new Map(cards.map((card) => [card.localId, card]));
    const trailingCards = cards.filter(
      (card) => !linkedLocalIds.has(card.localId)
    );
    const textSettled =
      showControls ||
      message.toolCalls.length > 0 ||
      /\n[ \t]*\n[ \t]*$/.test(text);
    const shownSegmentsRef = useRef<string[]>([]);
    const segments = holdStreamedSegments(
      shownSegmentsRef.current,
      textSettled ? allSegments : allSegments.slice(0, -1),
      textSettled
    );
    shownSegmentsRef.current = segments;
    const cardsFor = (localIds: Iterable<number>) =>
      [...localIds]
        .map((localId) => cardsByLocalId.get(localId))
        .filter((card) => card !== undefined);
    const views = segments.flatMap((segment): SegmentView[] => {
      const cardIds = segmentCardIds.has(segment)
        ? segmentCardIds.get(segment)
        : actionCardSegmentIds(segment);
      if (cardIds) {
        const linkedCards = cardsFor(cardIds);
        return linkedCards.length > 0
          ? [{ kind: 'cards', cards: linkedCards }]
          : [];
      }
      const view: SegmentView = isConnectionCardSegment(segment)
        ? { kind: 'connection', text: segment }
        : { kind: 'bubble', text: stripActionCardLinks(segment) };
      const inlineCards = cardsFor(findLinkedActionCardIds(segment));
      return inlineCards.length > 0
        ? [view, { kind: 'cards', cards: inlineCards }]
        : [view];
    });
    let lastBubbleIndex = -1;
    views.forEach((view, index) => {
      if (view.kind === 'bubble') lastBubbleIndex = index;
    });

    const animatesOnAppear =
      animatesOnAppearRef.current && !!(text || hasFiles);

    const [revealedCount, setRevealedCount] = useState(0);
    const lastRevealAtRef = useRef(0);
    const visibleViews = animatesOnAppear
      ? views.slice(0, revealedCount)
      : views;
    const cardsReady = trailingCards.length > 0 && showControls;
    const revealTarget = views.length + (cardsReady ? 1 : 0);
    const showCards =
      cardsReady && (!animatesOnAppear || revealedCount > views.length);
    useEffect(() => {
      if (!animatesOnAppear || revealedCount >= revealTarget) return;
      const sinceLast = Date.now() - lastRevealAtRef.current;
      const wait =
        revealedCount === 0 ? 0 : Math.max(0, ENTRANCE_STAGGER_MS - sinceLast);
      const timer = setTimeout(() => {
        lastRevealAtRef.current = Date.now();
        setRevealedCount((count) => count + 1);
      }, wait);
      return () => clearTimeout(timer);
    }, [animatesOnAppear, revealedCount, revealTarget]);

    const {
      handleCopyableEdit,
      handleAskSelection,
      handleImageLoaded,
      handleImageError,
      handleImageViewed,
      handleImageDownloaded,
    } = useAssistantMessageCallbacks(threadId, message);

    return (
      <Message
        data-message-index={messageIndex}
        data-message-id={message.id}
        data-role="assistant"
        className={precedesAssistantMessage ? 'mb-1.5' : 'mb-5'}
      >
        <MessageContent>
          {visibleViews.map((view, viewIndex) => {
            if (view.kind === 'cards') {
              return (
                <YStack
                  key={viewIndex}
                  className={cn(
                    'w-full gap-3',
                    messageMaxWidth,
                    animatesOnAppear &&
                      'animate-transcript-bubble-in motion-reduce:animate-none'
                  )}
                >
                  {view.cards.map((card) => (
                    <ActionCardItem
                      card={card}
                      key={card.id}
                      onAskUserComplete={onAskUserComplete}
                    />
                  ))}
                </YStack>
              );
            }
            if (view.kind === 'connection') {
              return (
                <div
                  key={viewIndex}
                  className={cn(
                    'w-full',
                    messageMaxWidth,
                    animatesOnAppear &&
                      'animate-transcript-bubble-in motion-reduce:animate-none'
                  )}
                >
                  <AssistantMarkdown
                    content={view.text}
                    threadId={threadId}
                    messageId={`${message.id}-part-${viewIndex}`}
                    onCopyableEdit={handleCopyableEdit}
                    onAskSelection={handleAskSelection}
                  />
                </div>
              );
            }
            const isLastBubble = viewIndex === lastBubbleIndex;
            return (
              <Bubble
                key={viewIndex}
                variant="outline"
                className={cn(
                  messageMaxWidth,
                  animatesOnAppear &&
                    'animate-transcript-bubble-in motion-reduce:animate-none'
                )}
              >
                {isLastBubble && (bubbleText || hasFiles) && showControls && (
                  <XStack className="pointer-events-none absolute top-1/2 left-full ml-3.5 -translate-y-1/2 items-center gap-0.5 opacity-0 touch:pointer-events-auto touch:opacity-100 group-hover/message:pointer-events-auto group-hover/message:opacity-100 has-[:focus-visible]:opacity-100 group-has-[:focus-visible]/message:opacity-100 group-has-[:focus-visible]/message:pointer-events-auto">
                    {bubbleText && (
                      <CopyButton
                        text={bubbleText}
                        copyText="Copy text"
                        className="flex"
                        size="xs"
                        data-testid="copy-response-button"
                        onCopy={() => {
                          trackEvent('MESSAGE_LB_COPY_CLICKED', {
                            chatId: threadId,
                            messageIndex,
                          });
                        }}
                      />
                    )}
                  </XStack>
                )}
                <BubbleContent className="rounded-2xl px-4 py-2 [&_.prose]:text-[13px]">
                  <AssistantMarkdown
                    content={view.text}
                    threadId={threadId}
                    messageId={`${message.id}-part-${viewIndex}`}
                    onCopyableEdit={handleCopyableEdit}
                    onAskSelection={handleAskSelection}
                  />
                </BubbleContent>
              </Bubble>
            );
          })}

          {askUserRequest && (
            <div className={cn('w-full', messageMaxWidth)}>
              <AskUserPicker
                request={askUserRequest}
                onComplete={onAskUserComplete}
              />
            </div>
          )}

          {message.fileIds.length > 0 ? (
            <Attachments
              fileIds={message.fileIds}
              isAssistant={true}
              pendingFiles={pendingSandboxFiles}
              onImageLoaded={handleImageLoaded}
              onImageError={handleImageError}
              onImageViewed={handleImageViewed}
              onImageDownloaded={handleImageDownloaded}
              key={message.id}
            />
          ) : (
            <PendingAssistantFiles files={pendingSandboxFiles} />
          )}

          {showCards && (
            <YStack
              className={cn(
                'gap-3',
                animatesOnAppear &&
                  'animate-transcript-bubble-in motion-reduce:animate-none'
              )}
            >
              {trailingCards.map((card) => (
                <ActionCardItem
                  card={card}
                  key={card.id}
                  onAskUserComplete={onAskUserComplete}
                />
              ))}
            </YStack>
          )}
        </MessageContent>
      </Message>
    );
  }
);

type AssistantMarkdownProps = {
  content: string;
  messageId: string;
  onAskSelection: (selection: { text: string }) => void;
  onCopyableEdit: (segmentIndex: number, newText: string) => void;
  threadId: string;
};

const AssistantMarkdown = memo(
  ({
    content,
    messageId,
    onAskSelection,
    onCopyableEdit,
    threadId,
  }: AssistantMarkdownProps) => (
    <YStack
      className={cn('text-foreground w-full gap-1', !content && 'hidden')}
      data-allow-selection
    >
      <Markdown
        id={`thread-${threadId}-assistant-message-${messageId}`}
        content={content || ' '}
        enableCopyableBlocks
        copyableBlocksEditable
        onCopyableEdit={onCopyableEdit}
        onAskSelection={onAskSelection}
        chatId={threadId}
      />
    </YStack>
  )
);
